import { Provider } from '@repo/api-gw-sdk';

import type { FieldConfig } from './fieldValidation';

// ---------- Patterns ----------
const guidPattern =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

const guidRegex = new RegExp(`^${guidPattern}$`, 'i');

const emailLikeRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Embed the body pattern where needed
const managedIdentityResourceIdRegex = new RegExp(
  `^/subscriptions/${guidPattern}/resourceGroups/[^/]+/providers/Microsoft\\.ManagedIdentity/userAssignedIdentities/[^/]+$`,
  'i'
);

// ---------- Central config ----------
const cloudFieldValidators = {
  [Provider.Azure]: {
    tenantId: {
      pattern: guidRegex,
      message:
        'Must be a tenant ID GUID in the form 8-4-4-4-12 hex digits, e.g. 12345678-90ab-cdef-1234-567890abcdef.',
      placeholder: 'Example: 8bcd6517-b18c-571f-9bff-61090439f4f9',
    },
    subscriptionId: {
      pattern: guidRegex,
      message:
        'Must be a subscription ID GUID in the form 8-4-4-4-12 hex digits, e.g. 12345678-90ab-cdef-1234-567890abcdef.',
      placeholder: 'Example: 6f20504e-f3b2-5313-8e59-2df489d63a8d',
    },
    applicationId: {
      pattern: guidRegex,
      message:
        'Must be an application (client) GUID in the form 8-4-4-4-12 hex digits, e.g. 12345678-90ab-cdef-1234-567890abcdef.',
      placeholder: 'Example: 22c2c01c-8a37-5938-8c5a-70a0a8d151c9',
    },
    servicePrincipalId: {
      pattern: guidRegex,
      message:
        'Must be a service principal GUID in the form 8-4-4-4-12 hex digits, e.g. 12345678-90ab-cdef-1234-567890abcdef.',
      placeholder: 'Example: acb6fcee-0160-551f-944c-d66e88248db3',
    },
    objectId: {
      pattern: guidRegex,
      message:
        'Must be an Azure AD object GUID in the form 8-4-4-4-12 hex digits, e.g. 12345678-90ab-cdef-1234-567890abcdef.',
      placeholder: 'Example: ab68ecfb-a022-5d84-8ff1-83b496bb7b8f',
    },
    managedIdentityResourceId: {
      pattern: managedIdentityResourceIdRegex,
      message:
        'Must be a full Azure resource ID for a user-assigned managed identity.',
      placeholder:
        'Example: /subscriptions/870d...50b4/resourceGroups/my-rg/providers/Microsoft.ManagedIdentity/userAssignedIdentities/my-identity',
    },
    resourceGroupName: {
      pattern: /^[a-zA-Z0-9._()-]+$/,
      message:
        'Must be a valid Azure resource group name using letters, digits, periods, underscores, hyphens, and parentheses.',
      placeholder: 'Example: my-resource-group',
    },
    managementGroupId: {
      pattern: guidRegex,
      message:
        'Must be a management group GUID in the form 8-4-4-4-12 hex digits, e.g. 12345678-90ab-cdef-1234-567890abcdef.',
      placeholder: 'Example: 3eca9074-011f-5008-9605-a7ccf00aa93e',
    },
    keyVaultUrl: {
      pattern: /^https:\/\/[a-z0-9-]+\.vault\.azure\.net\/?$/i,
      message:
        'Must be a valid Azure Key Vault URL, e.g. https://my-vault.vault.azure.net/',
      placeholder: 'Example: https://my-vault.vault.azure.net/',
    },
    secretUrl: {
      pattern:
        /^https:\/\/[a-z0-9-]+\.vault\.azure\.net\/secrets\/[a-zA-Z0-9-]+$/i,
      message:
        'Must be a valid Azure Key Vault secret URL, e.g. https://my-vault.vault.azure.net/secrets/my-secret.',
      placeholder:
        'Example: https://my-vault.vault.azure.net/secrets/my-secret',
    },
  },

  [Provider.Aws]: {
    accountId: {
      pattern: /^[0-9]{12}$/,
      message: 'Must be a 12-digit AWS account ID, e.g. 123456789012.',
      placeholder: 'Example: 123456789012',
    },
    organizationId: {
      // e.g. o-xxxxxxxxxxxx
      pattern: /^o-[a-z0-9]{10,32}$/,
      message: 'Must be an AWS organization ID like o-xxxxxxxxxxxx.',
      placeholder: 'Example: o-a1b2c3d4e5',
    },
    arn: {
      pattern: /^arn:[^:]+:[^:]*:[^:]*:[0-9]*:.*$/,
      message:
        'Must be a valid ARN, e.g. arn:aws:iam::123456789012:role/MyRole.',
      placeholder: 'Example: arn:aws:iam::123456789012:role/MyRole',
    },
    roleArn: {
      pattern: /^arn:aws:iam::[0-9]{12}:role\/[A-Za-z0-9+=,.@_\-/]+$/,
      message:
        'Must be a valid IAM role ARN, e.g. arn:aws:iam::123456789012:role/MyRole.',
      placeholder: 'Example: arn:aws:iam::123456789012:role/MyRole',
    },
    userArn: {
      pattern: /^arn:aws:iam::[0-9]{12}:user\/[A-Za-z0-9+=,.@_\-/]+$/,
      message:
        'Must be a valid IAM user ARN, e.g. arn:aws:iam::123456789012:user/MyUser.',
      placeholder: 'Example: arn:aws:iam::123456789012:user/MyUser',
    },
    kmsKeyArn: {
      pattern:
        /^arn:aws:kms:[a-z0-9-]+:[0-9]{12}:(key\/(mrk-)?[0-9a-f-]+|alias\/[a-zA-Z0-9/_-]+)$/,
      message:
        'Must be a valid KMS key or alias ARN, e.g. arn:aws:kms:us-east-1:123456789012:key/12345678-90ab-cdef-1234-567890abcdef or arn:aws:kms:us-east-1:123456789012:alias/my-key-alias.',
      placeholder:
        'Example: arn:aws:kms:us-east-1:123456789012:key/12345678-90ab-cdef-1234-567890abcdef',
    },
    ami: {
      pattern: /^ami-[0-9a-f]{8,17}$/,
      message: 'Must be a valid AMI ID, e.g. ami-0abcdef1234567890.',
      placeholder: 'Example: ami-0abcdef1234567890',
    },
    amiName: {
      // See https://docs.aws.amazon.com/AWSEC2/latest/APIReference/API_RegisterImage.html#:~:text=Name,Yes
      pattern: /^[a-zA-Z0-9()[\] .\-/'@_]{3,128}$/,
      message:
        "AMI names must be between 3 and 128 characters long, and may contain letters, numbers, spaces, '(', ')', '[', ']', '.', '-', '/', '@', '_', and single quotes.",
      placeholder: 'Example: my-ami-name',
    },
  },

  [Provider.Gcp]: {
    projectId: {
      pattern: /^[a-z][a-z0-9-]{5,29}$/,
      message:
        'Must be a valid GCP project ID: 6–30 chars, lowercase letters, digits, and hyphens, starting with a letter, e.g. my-project-123.',
      placeholder: 'Example: my-project-123',
    },
    projectNumber: {
      pattern: /^[0-9]{6,30}$/,
      message: 'Must be a numeric project number, e.g. 1234567890123.',
      placeholder: 'Example: 1234567890123',
    },
    organizationId: {
      pattern: /^[0-9]{6,30}$/,
      message: 'Must be a numeric organization ID, e.g. 123456789012.',
      placeholder: 'Example: 123456789012',
    },
    folderId: {
      pattern: /^[0-9]{6,30}$/,
      message: 'Must be a numeric folder ID, e.g. 123456789012.',
      placeholder: 'Example: 123456789012',
    },
    serviceAccountEmail: {
      pattern: emailLikeRegex,
      message:
        'Must be a valid service account email, e.g. my-sa@my-project.iam.gserviceaccount.com.',
      placeholder: 'Example: my-sa@my-project.iam.gserviceaccount.com',
    },
    kmsKeyResourceName: {
      pattern:
        /^projects\/[^/]+\/locations\/[^/]+\/keyRings\/[^/]+\/cryptoKeys\/[^/]+$/,
      message:
        'Must be a valid GCP KMS key resource name, e.g. projects/my-project/locations/us-central1/keyRings/my-ring/cryptoKeys/my-key.',
      placeholder:
        'Example: projects/my-project/locations/us-central1/keyRings/my-ring/cryptoKeys/my-key',
    },
    bigQueryDatasetId: {
      pattern: /^[a-zA-Z0-9_]{1,1024}$/,
      message:
        'Must be a valid BigQuery dataset ID: letters, numbers, and underscores only. No spaces or special characters like -, &, @, or %.',
      placeholder: 'Example: my_dataset_123',
    },
  },
} as const;

type CloudFieldValidators = typeof cloudFieldValidators;
type CloudField<P extends keyof CloudFieldValidators> =
  keyof CloudFieldValidators[P];

interface CloudFieldValidationOptions {
  /**
   * Whether the field is required. If false, empty values will pass validation.
   * Default: false
   */
  required?: boolean;
  /**
   * Whether to allow leading/trailing whitespace. If false, the value will be validated as-is.
   * If true, leading/trailing whitespace will be trimmed before validation.
   * Note: Whitespace in the middle of the string is always validated by the pattern.
   * Default: false
   */
  allowWhitespace?: boolean;
}

/**
 * Get a field configuration object with validation and placeholder text
 * for account-level identifiers: tenant, subscription, account, org,
 * project, folder, application, servicePrincipal, etc.
 */
export function getCloudFieldConfig<P extends keyof CloudFieldValidators>(
  provider: P,
  field: CloudField<P>,
  options: CloudFieldValidationOptions = {}
): FieldConfig {
  const validators = cloudFieldValidators[provider];
  const rule = validators[field] as {
    pattern: RegExp;
    message: string;
    placeholder: string;
  };
  const { required = false, allowWhitespace = false } = options;

  return {
    validation: {
      validateInput: (value: string) => {
        const testValue = allowWhitespace ? value.trim() : value;
        // If empty (after trimming if allowed), only pass if not required
        if (!testValue) return !required;
        return rule.pattern.test(testValue);
      },
      errorMessage: rule.message,
    },
    placeholder: rule.placeholder,
  };
}
